import { useEffect, useState } from "react";
import { cotAxios } from "./api";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Trophy, Package, ShoppingCart, Calendar } from "lucide-react";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export default function CotacaoDashHomePage() {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState(() => {
    const d = new Date();
    return { ano: d.getFullYear(), mes: d.getMonth() + 1 };
  });

  useEffect(() => { carregar(); }, [filtro]);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get(`/cotacao/dashboard?ano=${filtro.ano}&mes=${filtro.mes}`);
      setDash(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar dashboard");
    } finally { setLoading(false); }
  };

  const maxEvol = Math.max(1, ...(dash?.evolucao_6m || []).map((e) => e.total));

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cot-dashboard-mensal">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><TrendingUp className="w-6 h-6 text-blue-700" /> Dashboard Mensal</h1>
          <p className="text-sm text-slate-500">Valores de compra, rankings e evolução</p>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-500" />
          <select
            value={filtro.mes} onChange={(e) => setFiltro({ ...filtro, mes: Number(e.target.value) })}
            className="border rounded px-2 py-1 text-sm" data-testid="filtro-mes"
          >
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select
            value={filtro.ano} onChange={(e) => setFiltro({ ...filtro, ano: Number(e.target.value) })}
            className="border rounded px-2 py-1 text-sm" data-testid="filtro-ano"
          >
            {[2024, 2025, 2026, 2027].map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : !dash ? null : (
        <>
          {/* Cards principais */}
          <div className="grid sm:grid-cols-3 gap-3 mb-6">
            <Card><CardContent className="p-4">
              <div className="text-xs text-slate-500 flex items-center gap-1"><ShoppingCart className="w-3 h-3" /> Pedidos no mês</div>
              <div className="font-bold text-2xl mt-1">{dash.qtd_pedidos}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="text-xs text-slate-500">Valor total de compras</div>
              <div className="font-bold text-2xl mt-1 text-blue-700">{brl(dash.valor_total_compras)}</div>
            </CardContent></Card>
            <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4">
              <div className="text-xs text-emerald-800 flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Saving referente Maior Custo</div>
              <div className="font-bold text-2xl mt-1 text-emerald-700">{brl(dash.economia_total)}</div>
            </CardContent></Card>
          </div>

          {/* Evolução 6 meses */}
          <Card className="mb-6"><CardContent className="p-4">
            <div className="font-semibold mb-3">Evolução dos últimos 6 meses</div>
            <div className="flex items-end gap-2 h-40" data-testid="grafico-evolucao">
              {(dash.evolucao_6m || []).map((e) => {
                const h = Math.max(4, (e.total / maxEvol) * 140);
                const isCur = e.ano === dash.ano && e.mes === dash.mes;
                return (
                  <div key={e.label} className="flex-1 flex flex-col items-center gap-1">
                    <div className="text-[10px] text-slate-500">{brl(e.total)}</div>
                    <div className={`w-full rounded-t transition-all ${isCur ? "bg-blue-700" : "bg-blue-300"}`} style={{ height: h + "px" }} />
                    <div className="text-[10px] text-slate-500">{e.label}</div>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>

          {/* Rankings */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card><CardContent className="p-4">
              <div className="font-semibold flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-amber-600" /> Ranking fornecedores</div>
              {(dash.ranking_fornecedores || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic py-2">Sem pedidos neste mês.</div>
              ) : (
                <div className="space-y-2">
                  {dash.ranking_fornecedores.map((f, i) => (
                    <div key={f.fornecedor_id} className="flex items-center justify-between text-sm border-b pb-1.5">
                      <div className="flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${i === 0 ? "bg-amber-500" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-amber-800" : "bg-slate-300"}`}>{i + 1}</span>
                        <span className="truncate">{f.fornecedor_nome}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{brl(f.total)}</div>
                        <div className="text-[10px] text-slate-500">{f.qtd_pedidos} pedido(s)</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>

            <Card><CardContent className="p-4">
              <div className="font-semibold flex items-center gap-2 mb-3"><Package className="w-4 h-4 text-blue-600" /> Produtos mais comprados</div>
              {(dash.ranking_produtos_qtd || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic py-2">Sem pedidos neste mês.</div>
              ) : (
                <div className="space-y-2">
                  {dash.ranking_produtos_qtd.map((p, i) => (
                    <div key={p.produto_id} className="flex items-center justify-between text-sm border-b pb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-slate-400 text-xs shrink-0">{i + 1}.</span>
                        <span className="truncate">
                          {p.codigo && <span className="text-[10px] font-mono bg-slate-100 px-1 rounded mr-1">{p.codigo}</span>}
                          {p.nome}
                        </span>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold">{p.qtd.toFixed(0)} {p.unidade || ""}</div>
                        <div className="text-[10px] text-slate-500">{brl(p.valor)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );
}
